import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";

export const AdministratorShortedEventCostComparisons = () => {
    const dispatch = useDispatch();

    const [shortedEventCostComparisons, setShortedEventCostComparisons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [onlyShowBroadcastedEventsFilterValue, setOnlyShowBroadcastedEventsFilterValue] = useState(true);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedTicketGroupId, setLastSelectedTicketGroupId] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 50,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };
    
    const getShortedEventCostComparisons = (onlyShowBroadcastedEvents) => {
        setIsLoading(true);

        if (onlyShowBroadcastedEvents === undefined) {
            onlyShowBroadcastedEvents = onlyShowBroadcastedEventsFilterValue;
        }

        const dataItem = {
            onlyShowBroadcastedEvents: onlyShowBroadcastedEvents,
        };

        fetch(`/api/administrator/pos/shortedeventcostcomparison`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setShortedEventCostComparisons(result);
                setTotalEventCount(result.length);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }
    
    const getGridData = () => {
        return shortedEventCostComparisons.slice(page.skip, page.take + page.skip);
    };
    
    const onlyShowBroadcastedEventsFilterValueChanged = (event) => {
        const value = event.value;

        setOnlyShowBroadcastedEventsFilterValue(value);
        getShortedEventCostComparisons(value);
    }

    const selectedValueChanged = (event, ticketGroupId, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let shortedEventCostComparisonsCopy = shortedEventCostComparisons;

            const itemIndex = shortedEventCostComparisonsCopy.findIndex(dataItem =>
                dataItem.ticketGroupId === ticketGroupId
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedTicketGroupId !== undefined
                && lastSelectedTicketGroupId !== ticketGroupId) {
                const lastSelectedItemIndex = shortedEventCostComparisonsCopy.findIndex(dataItem =>
                    dataItem.ticketGroupId === lastSelectedTicketGroupId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        shortedEventCostComparisonsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                shortedEventCostComparisonsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedTicketGroupId(ticketGroupId);
            }

            setShortedEventCostComparisons(shortedEventCostComparisonsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let shortedEventCostComparisonsCopy = shortedEventCostComparisons;

            shortedEventCostComparisonsCopy.forEach(dataItem =>
                dataItem.selected = checked
            );

            setShortedEventCostComparisons(shortedEventCostComparisonsCopy);

            setLastSelectedTicketGroupId(undefined);

            setIsLoading(false);
        }, 1);
    };

    const deleteTicketGroups = (ticketGroupIds) => {
        if (window.confirm("Are you sure you want to delete these ticket groups?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketGroupIds: ticketGroupIds,
        };

        fetch(`/api/administrator/pos/deleteticketgroups`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Ticket groups deleted from POS successfully");

                getShortedEventCostComparisons();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const deleteSelectedTicketGroups = () => {
        let ticketGroupIds = [];

        shortedEventCostComparisons.forEach(dataItem => {
            if (dataItem.selected === true) {
                ticketGroupIds.push(dataItem.ticketGroupId);
            }
        });

        if (ticketGroupIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No ticket groups selected");
            return;
        }

        deleteTicketGroups(ticketGroupIds);
    };

    const copyCostsToTicketGroups = (ticketGroups) => {
        if (window.confirm("Are you sure you want to copy the costs to these ticket groups?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketGroups: ticketGroups,
        };

        fetch(`/api/administrator/pos/updateticketgroupcosts`,
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                if (result.message) {
                    addNotificationWithTimeout(dispatch, "success", result.message);
                }
                else {
                    addNotificationWithTimeout(dispatch, "success", "Ticket group costs copied successfully");
                }                

                getShortedEventCostComparisons();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    }

    const copyCostsToSelectedTicketGroups = () => {
        let ticketGroups = [];

        shortedEventCostComparisons.forEach(dataItem => {
            if (dataItem.selected === true) {
                ticketGroups.push({
                    ticketGroupId: dataItem.ticketGroupId,
                    cost: dataItem.otherPersonCost,
                    price: dataItem.otherPersonPrice,
                });
            }
        });

        if (ticketGroups.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No ticket groups selected");
            return;
        }

        copyCostsToTicketGroups(ticketGroups);
    }

    const ViewUrlCell = (props) => {
        const url = props.dataItem[props.field];

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const SelectedCell = (props) => {
        const ticketGroupId = props.dataItem.ticketGroupId;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, ticketGroupId, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    useEffect(() => {
        document.title = "Shorted Event Cost Comparisons";

        getShortedEventCostComparisons();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></>}

            <div className="k-form k-form-md">
                <div className="k-form-fieldset container">
                    <div className="row">
                        <div className="col-12">
                            <div className="k-form-field">
                                <Checkbox
                                    label={"Only show broadcasted events"}
                                    onChange={onlyShowBroadcastedEventsFilterValueChanged}
                                    value={onlyShowBroadcastedEventsFilterValue}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="m-2 d-flex flex-row">
                <div className="me-2">
                    <Button type={"button"} icon="copy" title="Copy Costs to Selected Ticket Groups" onClick={copyCostsToSelectedTicketGroups}>
                        Copy Costs to Selected Ticket Groups
                    </Button>
                </div>
                <div className="me-2">
                    <Button type={"button"} icon="trash" title="Delete Selected Ticket Groups" onClick={deleteSelectedTicketGroups}>
                        Delete Selected Ticket Groups
                    </Button>
                </div>                
            </div>

            <Grid
                data={getGridData()}
                resizable={true}
                skip={page.skip}
                take={page.take}
                total={totalEventCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                <Column field="eventName" title={"Event"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="eventDate" title={"Date"} />
                <Column field="quantity" title={"Quantity"} />
                <Column field="myCost" title={"My Cost"} />
                <Column field="otherPersonCostFormatted" title={"Other Cost"} />
                <Column field="costDifferenceFormatted" title={"Difference"} />
            </Grid>
        </div>
    );
}